import React, { useState, useContext, useEffect, useRef } from "react";
import PropTypes from 'prop-types';
import { Turnstile } from '@marsidev/react-turnstile';
import "./AdDetailsContactSeller.css";
import { AuthContext } from "../../AuthContext";

const AdDetailsContactSeller = ({ sellerEmail, adTitle, adId, ownerName, adLink }) => {
    const { user } = useContext(AuthContext);
    const isOwner = user && user.email && sellerEmail && user.email.toLowerCase() === sellerEmail.toLowerCase();

    const [name, setName] = useState(user?.username || "");
    const [email, setEmail] = useState(user?.email || "");
    const [message, setMessage] = useState("");
    const [formError, setFormError] = useState("");
    const [apiError, setApiError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [isSent, setIsSent] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState("");
    const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
    const turnstileRef = useRef(null);

    // If the user context loads after render, update fields
    useEffect(() => {
        if (user) {
            setName(user.username || "");
            setEmail(user.email || "");
        }
    }, [user]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError("");
        setApiError("");
        setSuccessMessage("");
        setIsLoading(true);

        if (!name.trim() || !email.trim() || !message.trim()) {
            setFormError("Vsa polja morajo biti izpolnjena.");
            setIsLoading(false);
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setFormError("Prosimo, vnesite veljaven email naslov.");
            return;
        }

        if (!sellerEmail) {
            setApiError("Napaka: Email prodajalca ni na voljo. Poskusite osvežiti stran.");
            console.error("Missing sellerEmail prop");
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/contact-seller`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name,
                    email,
                    message,
                    adId,
                    sellerEmail,
                    adTitle: adTitle || 'neimenovan oglas',
                    ownerName,
                    adLink,
                    'cf-turnstile-response': turnstileToken,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                setApiError(data.error || `Napaka pri pošiljanju: ${response.statusText}`);
            } else {
                setIsSent(true);
                setMessage("");
                setIsCaptchaVerified(false);
                setTurnstileToken("");
                turnstileRef.current?.reset();
            }
        } catch (error) {
            console.error("Network or other error:", error);
            setApiError("Prišlo je do napake v omrežju. Poskusite znova.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="contact_seller">
            {/* NOVO: Pogojno prikazovanje glede na to, ali je uporabnik lastnik */}
            {isOwner ? (
                // PRIKAZ ZA LASTNIKA OGLASA
                <div className="owner_view">
                    <h5 className="contact_seller_title">⁍ To je vaš oglas</h5>
                    <p className="owner_view_info">
                        Na tem mestu bodo drugi uporabniki stopili v stik z vami. Obrazec za pošiljanje sporočil je viden samo potencialnim kupcem.
                    </p>
                </div>
            ) : (
                // OBSTOJEČI PRIKAZ ZA VSE OSTALE UPORABNIKE
                <>
                    {isSent && (
                        <div className="success_overlay">
                            <div className="success_content">
                                <i className="fa-solid fa-circle-check success_icon"></i>
                                <h4>Sporočilo poslano!</h4>
                                <p>Prodajalec je prejel vaše sporočilo.</p>
                            </div>
                        </div>
                    )}
                    <h5 className="contact_seller_title">⁍ Kontaktiraj prodajalca</h5>
                    <form className={`contact_seller_form ${isSent ? 'faded_form' : ''}`} onSubmit={handleSubmit}>
                        {formError && <p className="error_message validation_error">{formError}</p>}
                        {apiError && <p className="error_message api_error">{apiError}</p>}
                        {successMessage && <p className="success_message">{successMessage}</p>}

                        {/* Vsi inputi in gumb ostanejo nespremenjeni */}
                        <input
                            type="text"
                            className="contact_input"
                            placeholder="Ime"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={isLoading || isSent}
                        />
                        <input
                            type="email"
                            className="contact_input"
                            placeholder="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={isLoading || isSent}
                        />
                        <textarea
                            className="contact_textarea"
                            placeholder="Sporočilo"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            disabled={isLoading || isSent}
                        />
                        <div style={{ opacity: isSent ? 0 : 1, transition: 'opacity 0.3s' }}>
                            <Turnstile
                                ref={turnstileRef}
                                siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'}
                                style={{ transform: 'scale(0.9)', transformOrigin: '0 0', marginTop: '10px' }}
                                onSuccess={(token) => {
                                    setIsCaptchaVerified(true);
                                    setTurnstileToken(token);
                                }}
                                onExpire={() => {
                                    setIsCaptchaVerified(false);
                                    setTurnstileToken("");
                                }}
                                onError={() => {
                                    setApiError("Napaka pri nalaganju varnostne komponente. Osvežite stran.");
                                    setIsCaptchaVerified(false);
                                }}
                                options={{ theme: 'light' }}
                            />
                        </div>

                        <button
                            type="submit"
                            className="btn btn-success contact_submit"
                            disabled={isLoading || !isCaptchaVerified}
                        >
                            {isLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                    Pošiljanje...
                                </>
                            ) : (
                                <>
                                    <i className="fa-regular fa-paper-plane"></i>
                                    Pošlji
                                </>
                            )}
                        </button>
                    </form>
                </>
            )}
        </div>
    );
};

AdDetailsContactSeller.propTypes = {
    sellerEmail: PropTypes.string.isRequired,
    adTitle: PropTypes.string,
    adId: PropTypes.string.isRequired,
    ownerName: PropTypes.string,
    adLink: PropTypes.string,
};

export default AdDetailsContactSeller;